<template>
    <div class="container">
        <v-title :lineage="[{'Value Lists':'value-lists'}]">
            Version {{ version }} -
            {{ id | truncate({dots: true}) }}
            <div slot="actions">
                <r-button
                    v-if="showRestoreButton"
                    type="primary"
                    class="inline-s"
                    :disabled="isLoading"
                    @click="handleRestoreVersion">Restore version {{ version }}
                </r-button>
            </div>
        </v-title>

        <div class="grid">
            <div class="grid-item grid-item-secondary">
                <div class="tile">
                    <div class="tile-content position-relative">
                        <versions-list-filter
                            v-if="currentList.version"
                            :version="activeVersion"
                            :filter-params="listVersionsFilter"
                            :items="listVersions"
                            :is-loading="isLoading"
                            date-filter-key-name="updatedTime"
                            empty-message="No rules exist at this date"
                            v-on:filter="handleUpdateHistoryFilter"
                            v-on:navigate="handleNavigateToVersion"/>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-primary">
                <div class="tile">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    label="List Description"
                                    v-model="selectedList.description"
                                    disabled
                                    help-text="As displayed in the rules engine condition selection."
                                />
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    label="List ID"
                                    v-model="selectedList.id"
                                    disabled
                                    help-text="An ID will be automatically generated if you leave this field empty"
                                />
                            </div>
                            <div class="grid-item grid-item-full">

                                <r-input
                                    v-if="showValuesAreaPreview"
                                    label="Values"
                                    rows="8"
                                    :value="listValuesString"
                                    :multiline="true"
                                    :help-text="`Number of values: ${listValuesCount}`"
                                    disabled
                                />
                                <div v-else>
                                    <div class="divider"></div>
                                    <p>Your list holds <b>{{ listValuesCount | number }}</b> values.</p>
                                    <p class="stack-m">Large list of <b>{{ maxPreviewValues | number }}</b> or
                                        more values cannot
                                        be displayed.</p>
                                    <r-button
                                        v-if="!isLoading"
                                        size="small"
                                        @click="handleDownloadCSV">Download values as CSV file
                                    </r-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {createDialog, vConfirm} from '@/components/modal/dialogs/index';
    import vTitle from '@/components/v-title';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import ListModel from '@/common/models/list-model';
    import Media from '@/common/files/media';

    import VersionsListFilter from '../components/common/versions-list-filter';
    import {maxManualEditableValues} from './file-import-settings';

    const defaultTrackingParams = {
        limit: 5,
        offset: 0,
        sort: '-version',
    };

    export default {
        name: 'list-history',
        mixins: [LoadStateMixin],
        components: {
            vTitle,
            VersionsListFilter,
        },
        props: {
            id: {
                type: String,
                required: true,
            },
            version: {
                type: [String, Number],
                required: true,
            },
        },
        data() {
            return {
                currentList: new ListModel(), // actual list model
                selectedList: new ListModel(), // selected history list model
                listVersions: [],
                maxPreviewValues: maxManualEditableValues,
                isLoading: false,
                listVersionsFilter: this.getDefaultListVersionsFilter(),
            };
        },
        computed: {
            activeVersion() {
                return +this.version;
            },
            showRestoreButton() {
                return this.currentList.version
                    && this.selectedList.version
                    && this.currentList.version !== this.selectedList.version;
            },
            listValuesCount() {
                return this.selectedList.values.length;
            },
            // returns true if values is showed in the textarea
            showValuesAreaPreview() {
                return this.listValuesCount <= this.maxPreviewValues;
            },
            // textarea values string
            listValuesString() {
                return this.showValuesAreaPreview
                    ? this.selectedList.values.join()
                    : '';
            },
        },
        methods: {
            handleNavigateToVersion({version}) {
                this.$router.push({name: 'history-list-item', params: {id: this.id, version}});
            },
            getDefaultListVersionsFilter() {
                return {
                    filter: {id: this.id},
                    ...defaultTrackingParams,
                };
            },
            handleUpdateHistoryFilter(listVersionsFilter) {
                this.listVersionsFilter = listVersionsFilter;
                this.$nextTick(() => {
                    this.fetchData();
                });
            },
            async handleRestoreVersion() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Restore List',
                    content: `Are you sure you want to restore the current list to this version? The change will be applied immediately.`,
                    confirmLabel: 'Yes',
                };

                const isConfirmed = await confirmDialog(dialogParams);

                if (!isConfirmed) {
                    return;
                }

                this.isLoading = true;
                try {
                    const result = await this.$api(({lists}) => lists.update({
                        id: this.id,
                        data: this.selectedList.getPayload(),
                    }));

                    this.currentList = new ListModel(result.fields);
                    this.selectedList = new ListModel(result.fields);

                    this.$toast.positive(`Version ${this.version} has been restored.`);

                    this.$router.push({
                        name: 'history-list-item',
                        params: {id: this.id, version: this.currentList.version},
                    });
                } catch (e) {
                    this.isLoading = false;
                    this.currentList = new ListModel();
                    this.selectedList = new ListModel();
                    this.fetchData();
                }
            },
            handleDownloadCSV() {
                const fileName = `list-${this.id}-version-${this.version}-${this.$tz().now()}`;

                const data = this.list.values.reduce((memo, row) => {
                    memo += `${row}\n`;
                    return memo;
                }, '');

                const media = new Media({
                    mimeType: 'text/csv',
                    data,
                    fileName,
                });

                media.triggerDownload();
            },
            async fetchData() {
                this.isLoading = true;

                try {
                    const listVersionsFilter = {
                        ...this.getDefaultListVersionsFilter(),
                        ...this.listVersionsFilter,
                    };
                    // prepare filter
                    listVersionsFilter.filter = Object.keys(listVersionsFilter.filter)
                        .reduce((memo, key) => [...memo, `${key}:${listVersionsFilter.filter[key]}`], [])
                        .join(';');

                    const currentListIsLoaded = !!this.currentList.id;
                    const selectedListIsLoaded = !!this.selectedList.id;

                    const getHistory = this.$api(
                        ({tracking}) => tracking.getAllListsChangesHistory(listVersionsFilter),
                    );

                    const getCurrentList = !currentListIsLoaded
                        ? this.$api(({lists}) => lists.get({id: this.id}))
                        : Promise.resolve();

                    const getSelectedList = !selectedListIsLoaded
                        ? this.$api(({lists}) => lists.get({
                            id: this.id,
                            version: this.version,
                        }))
                        : Promise.resolve();

                    const [
                        currentList,
                        selectedList,
                        history,
                    ] = await Promise.all([getCurrentList, getSelectedList, getHistory]);

                    if (!currentListIsLoaded) {
                        // active list model
                        this.currentList = new ListModel(currentList.fields);
                    }
                    if (!selectedListIsLoaded) {
                        // selected list model
                        this.selectedList = new ListModel(selectedList.fields);
                    }

                    this.listVersions = history.items.map(item => new ListModel(item.fields));
                    this.isLoading = false;
                } catch (error) {
                    this.isLoading = false;
                    this.$redirectOnError(error, {name: 'edit-list-item', params: {id: this.id}});
                }
            },
        },
        watch: {
            '$route.params.version': (version) => {
                const selectedListModel = this.listVersions.find(item => item.version === parseInt(version, 10));

                if (selectedListModel) {
                    this.selectedList = selectedListModel;
                    return;
                }

                this.fetchData();
            },
        },
    };
</script>
